// React hook for the web FTUE / Settings network-feed recommendation surface
// (FTUE Network Feed Analysis spec; web read/apply task).
//
// Reads `GET /v1/boats/:boatId/source-analysis/latest` and re-fetches whenever
// the cloud publishes a `source_analysis_ready` notification on the boat's
// `boat.<id>.devices` user-WS topic (the same topic + live-update pattern
// useBoatDevices.ts uses — the notify rides that topic by design). `apply` POSTs
// the confirmed `NetworkApplyDecisions` to the cloud apply endpoint, which
// relays it to the boat as a `source_plan_push`.
//
// A 404 from /latest is the "no analysis yet" state, not an error — the surface
// renders its "Tune your network" empty entry from it.

import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api';
import { beforeWsReconnect } from './wsReconnect';
import type {
  NetworkApplyDecisions,
  NetworkUserConstraints,
  SourceFeedRecommendation,
} from './networkRecommendation';

export type SourceAnalysisState =
  | { kind: 'loading' }
  | { kind: 'none' } // no analysis computed yet (404)
  | { kind: 'ready'; recommendation: SourceFeedRecommendation }
  | { kind: 'error'; message: string };

export interface UseSourceAnalysis {
  state: SourceAnalysisState;
  refresh: () => void;
  /** POST the confirmed plan to cloud; resolves on a relayed (202) response. */
  apply: (decisions: NetworkApplyDecisions) => Promise<void>;
  /**
   * POST the user's edits as HARD constraints to `.../reevaluate`; the cloud
   * re-runs the AI and lands a NEW (always-valid) analysis on the same
   * `source_analysis_ready` notify. Resolves once the cloud accepts (200); on a
   * 404 (`no_snapshot`) / 4xx it re-throws so the surface can show the error.
   */
  reevaluate: (constraints: NetworkUserConstraints) => Promise<void>;
  /** True from an accepted reevaluate until its reevaluated result lands. */
  reevaluating: boolean;
}

const BACKOFF_INITIAL_MS = 1_000;
const BACKOFF_MAX_MS = 16_000;

export function useSourceAnalysis(boatId: string): UseSourceAnalysis {
  const [state, setState] = useState<SourceAnalysisState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // A reevaluate is "in flight" from the accepted POST until its reevaluated
  // (terminal) result lands. `reevaluatingRef` mirrors the state so the async
  // GET effect below reads the live value, not a stale render-time closure.
  const [reevaluating, setReevaluating] = useState(false);
  const reevaluatingRef = useRef(false);
  // The requestId the in-flight reevaluate was launched FROM. `/latest` skips the
  // interim 'analyzing' placeholder, so mid-reevaluate it keeps returning this
  // (prior, terminal) rec — which must NOT end the reevaluate. Only a rec with a
  // DIFFERENT requestId is the reevaluated result. `null` = reevaluate from a
  // non-ready state (no prior id), where any terminal rec ends it.
  const reevaluateFromRef = useRef<string | null>(null);

  // GET the latest recommendation. Re-runs on tick / boatId.
  useEffect(() => {
    if (boatId === '') return;
    let cancelled = false;
    // A normal (re)fetch resets to 'loading'. But a reevaluate's refresh()
    // re-polls while /latest still returns the cloud's interim 'analyzing'
    // placeholder row — flashing 'loading' here would tear down the terminal
    // card the user is editing. So mid-reevaluate, hold a displayed terminal rec
    // in place; the GET below swaps in the reevaluated result when it lands.
    setState((prev) =>
      reevaluatingRef.current && prev.kind === 'ready' && prev.recommendation.status !== 'analyzing'
        ? prev
        : { kind: 'loading' }
    );
    void (async () => {
      try {
        const rec = await api<SourceFeedRecommendation>(
          `/v1/boats/${boatId}/source-analysis/latest`
        );
        if (cancelled) return;
        // A terminal (ready/degraded/failed) result with a NEW requestId ends an
        // in-flight reevaluate — the reevaluated recommendation has arrived. The
        // prior rec keeps coming back (it's terminal and /latest skips the interim
        // 'analyzing' placeholder), so gate on the id CHANGING; otherwise we'd
        // clear the flag on the first re-poll and the "Re-analyzing…" state would
        // never show. `reevaluateFromRef === null` (reevaluate from a non-ready
        // state) falls back to "any terminal rec ends it".
        if (
          rec.status !== 'analyzing' &&
          reevaluatingRef.current &&
          rec.requestId !== reevaluateFromRef.current
        ) {
          reevaluatingRef.current = false;
          setReevaluating(false);
        }
        // An 'analyzing' placeholder (empty rows/overlap) is NOT a result — the
        // job is still computing. Treat it like the 404 'none' state so the
        // determining beat keeps waiting instead of rendering a broken empty
        // pick. (The server also filters these from /latest; this is belt-and-
        // braces against a placeholder slipping through.) EXCEPTION: mid-
        // reevaluate, hold the displayed terminal card (with the user's edits) up
        // under a "Re-analyzing…" CTA rather than tearing it down to 'none' — the
        // reevaluated terminal result swaps it in when it lands.
        if (rec.status === 'analyzing') {
          setState((prev) =>
            reevaluatingRef.current &&
            prev.kind === 'ready' &&
            prev.recommendation.status !== 'analyzing'
              ? prev
              : { kind: 'none' }
          );
          return;
        }
        setState({ kind: 'ready', recommendation: rec });
      } catch (err) {
        if (cancelled) return;
        const e = err as { status?: number; message?: string };
        if (e.status === 404) {
          setState({ kind: 'none' });
          return;
        }
        const message =
          e.status === 403
            ? 'You do not have access to this boat.'
            : e.status === 401
              ? 'Please sign in again.'
              : (e.message ?? 'Failed to load the network analysis.');
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boatId, tick]);

  // Subscribe to the boat's devices topic; a `source_analysis_ready` notify
  // arrives as a `devices` frame (user-ws.ts maps the topic). Bump tick to
  // re-GET. Bounded-backoff auto-reconnect — mirrors useBoatDevices.
  useEffect(() => {
    if (boatId === '' || typeof WebSocket === 'undefined') return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = BACKOFF_INITIAL_MS;
    let cancelled = false;

    function scheduleReconnect(): void {
      if (cancelled) return;
      const delay = backoffMs;
      backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
      reconnectTimer = setTimeout(connect, delay);
    }

    function connect(): void {
      if (cancelled) return;
      const url = new URL('/v1/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      let ws: WebSocket;
      try {
        ws = new WebSocket(url.toString());
      } catch {
        scheduleReconnect();
        return;
      }
      socket = ws;
      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        backoffMs = BACKOFF_INITIAL_MS;
        try {
          ws.send(JSON.stringify({ type: 'subscribe', topic: `boat.${boatId}.devices` }));
        } catch {
          // socket gone; onclose will retry
        }
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') return;
        try {
          const frame = JSON.parse(ev.data) as { type?: string; payload?: { kind?: string } };
          // Only the analysis-ready notify is interesting here; ignore the
          // device inventory/liveness churn that also rides this topic.
          if (frame.type === 'devices' && frame.payload?.kind === 'source_analysis_ready') {
            setTick((t) => t + 1);
          }
        } catch {
          /* ignore */
        }
      };
      ws.onclose = (ev) => {
        socket = null;
        // Renew before retrying when the cloud closed us for an expired access
        // token (task ws_session_expiry): reconnecting with the cookie it just
        // refused only earns another refusal, and the screen would sit stale
        // through the backoff. Reconnects either way.
        void beforeWsReconnect(ev.code).then(scheduleReconnect, scheduleReconnect);
      };
      ws.onerror = () => {
        // onclose handles reconnect.
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (socket !== null) {
        try {
          socket.close();
        } catch {
          /* noop */
        }
        socket = null;
      }
    };
  }, [boatId]);

  const apply = useCallback(
    async (decisions: NetworkApplyDecisions): Promise<void> => {
      await api(`/v1/boats/${boatId}/source-analysis/apply`, {
        method: 'POST',
        body: decisions,
      });
      // The boat applies + re-syncs its state to cloud; re-read to close the
      // loop once the (coalesced) state lands. Optimistic refresh now; the WS
      // notify will refresh again when the new analysis/state is durable.
      refresh();
    },
    [boatId, refresh]
  );

  const reevaluate = useCallback(
    async (constraints: NetworkUserConstraints): Promise<void> => {
      // Pin the reevaluate to the recommendation the user is looking at when we
      // have a ready one (only a 'ready' rec carries a requestId), so the cloud
      // reevaluates THAT snapshot; otherwise omit it and let the cloud reevaluate
      // its latest. JSON.stringify drops the undefined key, so the body ships
      // without `requestId` in that case.
      const requestId = state.kind === 'ready' ? state.recommendation.requestId : undefined;
      try {
        await api(`/v1/boats/${boatId}/source-analysis/reevaluate`, {
          method: 'POST',
          body: { requestId, constraints },
        });
        // Accepted: the cloud is re-running the AI. Remember the id we're
        // reevaluating FROM so the continuity guard holds the current terminal
        // card up (under "Re-analyzing…") and only ends the flag when a rec with a
        // NEW id — the reevaluated result — lands.
        reevaluateFromRef.current = requestId ?? null;
        reevaluatingRef.current = true;
        setReevaluating(true);
        refresh();
      } catch (err) {
        // 404 'no_snapshot' / 4xx surface through api()'s thrown { status, body };
        // clear the in-flight flag and re-throw so the card can show the error.
        reevaluatingRef.current = false;
        setReevaluating(false);
        throw err;
      }
    },
    [boatId, refresh, state]
  );

  return { state, refresh, apply, reevaluate, reevaluating };
}
